import clsx from "clsx";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import HideIcon from "../assets/icons/hide.svg";
import ShowIcon from "../assets/icons/show.svg";

interface CustomInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onChange" | "value" | "className"
  > {
  label?: string;
  type?: string;
  name?: string;
  id?: string;
  value: string;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Applied to the outer field wrapper (`.d-field`). */
  className?: string;
  /** Applied to the native `<input>` (`.d-input`). */
  inputClassName?: string;
  /** Error message — when present, the control turns red and shows the text. */
  error?: ReactNode;
  /** Helper text shown under the control when there is no error. */
  helper?: ReactNode;
  /** Reserve one line of space under the control so the form doesn't jump when
   *  an error appears/disappears (validate-on-blur forms). */
  reserveMessageSpace?: boolean;
  /** Renders a red "*" after the label. */
  required?: boolean;
  /** Auth-only: renders a "Forgot your password?" link in the label row. */
  forgotLink?: boolean;
  /** Right-side slot in the label row (e.g. the root-language warning) —
   *  the label is flex justify-between, so this lands flush right. */
  labelAccessory?: ReactNode;
  disabled?: boolean;
  /** Decorative glyph inside the left edge of the control. */
  leadingIcon?: ReactNode;
  /** Glyph inside the right edge (ignored for password fields, which render
   *  their own show/hide toggle). */
  trailingIcon?: ReactNode;
}

/**
 * Canonical text field for the app — the single shared `Input` the UI audit
 * (Section 5) asked for. Styled via the kit's `.d-field` / `.d-input` classes
 * (radius `--d-r-md` 6px, hairline `--d-rule` border, `--d-accent` focus ring)
 * so every form reads as one design system. Supports text/password/number/etc.
 * via `type`, plus label / error / helper / leading & trailing icon slots.
 */
export default function CustomInput({
  label,
  type = "text",
  name,
  id,
  value,
  placeholder,
  onChange,
  className,
  inputClassName = "",
  error,
  helper,
  reserveMessageSpace,
  required,
  forgotLink,
  labelAccessory,
  disabled,
  leadingIcon,
  trailingIcon,
  maxLength,
  ...props
}: CustomInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const resolvedType = isPassword ? (showPassword ? "text" : "password") : type;
  const hasError = Boolean(error);

  // Password fields render a built-in show/hide toggle as their trailing
  // adornment; it takes precedence over a caller-supplied trailingIcon.
  const rightAdornment = isPassword ? (
    <button
      type="button"
      className="right-icon"
      onClick={() => setShowPassword((prev) => !prev)}
      aria-label={showPassword ? "Hide password" : "Show password"}
    >
      <img src={!showPassword ? HideIcon : ShowIcon} alt="" />
    </button>
  ) : trailingIcon ? (
    <span className="right-icon">{trailingIcon}</span>
  ) : null;

  return (
    <div className={clsx("d-field", className)}>
      {label && (
        <label htmlFor={id} className="d-field-label">
          <span>
            {label}
            {required && <span className="req">*</span>}
          </span>
          {forgotLink && (
            <Link to="/forgot-password" className="underline font-medium">
              Forgot your password?
            </Link>
          )}
          {labelAccessory}
        </label>
      )}

      <div className="d-input-wrap">
        {leadingIcon && <span className="left-icon">{leadingIcon}</span>}
        <input
          type={resolvedType}
          name={name}
          id={id}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          disabled={disabled}
          // Respect the caller's maxLength; fall back to the historical 40-char
          // default only when none is supplied (previously 40 was hardcoded and
          // silently overrode the prop).
          maxLength={maxLength ?? 40}
          aria-invalid={hasError || undefined}
          {...props}
          className={clsx("d-input", hasError && "is-error", inputClassName)}
        />
        {rightAdornment}
      </div>

      {hasError ? (
        <p className="d-field-error">{error}</p>
      ) : helper ? (
        <p className="d-field-helper">{helper}</p>
      ) : reserveMessageSpace ? (
        // Empty one-line placeholder keeps the field height stable so the form
        // doesn't shift when a validation message appears.
        <p className="d-field-error" aria-hidden="true">
          &nbsp;
        </p>
      ) : null}
    </div>
  );
}
